import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { VscComment, VscHeart, VscHeartFilled, VscSend } from "react-icons/vsc";
import imageCompression from "browser-image-compression";
import { supabase } from "../../supabase/supabaseClient";
import {
    addComment,
    addPostMedia,
    addReaction,
    createPost,
    deletePostWithMediaCleanup,
    getComments,
    getPosts,
    removeReaction
} from "../../services/postsService";
import { listConversations, sendMessage } from "../../services/messagesService";
import { getConnectionsForCurrentUser, sendConnectionRequest } from "../../services/connectionsService";
import "./Home.css";

function Home() {
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);
    const [showPostModal, setShowPostModal] = useState(false);
    const [postContent, setPostContent] = useState("");
    const [postMediaFile, setPostMediaFile] = useState(null);
    const [postSubmitting, setPostSubmitting] = useState(false);
    const [feedPosts, setFeedPosts] = useState([]);
    const [feedError, setFeedError] = useState("");
    const [feedLoading, setFeedLoading] = useState(true);
    const [openPostMenuId, setOpenPostMenuId] = useState("");
    const [deletingPostId, setDeletingPostId] = useState("");
    const [likeLoadingPostId, setLikeLoadingPostId] = useState(null);
    const [expandedComments, setExpandedComments] = useState({});
    const [commentsByPost, setCommentsByPost] = useState({});
    const [commentDraftByPost, setCommentDraftByPost] = useState({});
    const [commentsLoadingByPost, setCommentsLoadingByPost] = useState({});
    const [commentSubmittingPostId, setCommentSubmittingPostId] = useState(null);
    const [activePostModal, setActivePostModal] = useState(null);
    const [activePostMediaIndex, setActivePostMediaIndex] = useState(0);
    const [sendModalPost, setSendModalPost] = useState(null);
    const [sendTargets, setSendTargets] = useState([]);
    const [selectedSendTarget, setSelectedSendTarget] = useState("");
    const [sendLoading, setSendLoading] = useState(false);
    const [suggestedConnections, setSuggestedConnections] = useState([]);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [suggestedScouts, setSuggestedScouts] = useState([]);
    const [scoutsLoading, setScoutsLoading] = useState(false);
    const [connectLoadingUserId, setConnectLoadingUserId] = useState("");
    const [profileSummary, setProfileSummary] = useState({
        name: "",
        role: "",
        position: "",
        location: "",
        avatarUrl: ""
    });
    const [orgPostIdentity, setOrgPostIdentity] = useState(null);
    const [selectedPostAs, setSelectedPostAs] = useState("");
    const [focusedPreviewPost, setFocusedPreviewPost] = useState(null);
    const [highlightedPostId, setHighlightedPostId] = useState(null);
    const postMediaInputRef = useRef(null);
    const focusPostId = location.state?.focusPostId || "";
    const focusPostPreview = location.state?.focusPostPreview || null;

    const formatTimeAgo = (dateValue) => {
        if (!dateValue) return "";

        const date = new Date(dateValue);
        if (Number.isNaN(date.getTime())) return "";

        const diffMs = Date.now() - date.getTime();
        const diffMinutes = Math.floor(diffMs / (1000 * 60));

        if (diffMinutes < 1) return "Just now";
        if (diffMinutes < 60) return `${diffMinutes}m ago`;

        const diffHours = Math.floor(diffMinutes / 60);
        if (diffHours < 24) return `${diffHours}h ago`;

        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays}d ago`;
    };

    const uploadPostMediaFile = async (file, userId) => {
        if (!file) return { data: null, error: null };

        if (!file.type.startsWith("image/")) {
            return {
                data: null,
                error: new Error("Only image uploads are supported for posts.")
            };
        }

        let uploadFile = file;
        try {
            uploadFile = await imageCompression(file, {
                maxSizeMB: 1,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                initialQuality: 0.82,
            });
        } catch (compressionError) {
            console.error("Image compression failed, using original file:", compressionError);
            uploadFile = file;
        }

        const fileExt = (uploadFile.name.split(".").pop() || "bin").toLowerCase();
        const sanitizedExt = fileExt.replace(/[^a-z0-9]/g, "") || "bin";
        const mediaType = "image";

        const filePath = `posts/${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${sanitizedExt}`;
        const bucketCandidates = ["post-media", "post_media", "posts", "media"];
        const uploadErrors = [];

        for (const bucket of bucketCandidates) {
            const { error: uploadError } = await supabase
                .storage
                .from(bucket)
                .upload(filePath, uploadFile, {
                    upsert: false,
                    contentType: uploadFile.type || undefined,
                });

            if (uploadError) {
                uploadErrors.push(`${bucket}: ${uploadError.message || "upload failed"}`);
                continue;
            }

            const { data: publicUrlData } = supabase
                .storage
                .from(bucket)
                .getPublicUrl(filePath);

            return {
                data: {
                    mediaType,
                    url: publicUrlData?.publicUrl || ""
                },
                error: null
            };
        }

        return {
            data: null,
            error: new Error(`Storage upload failed. Tried buckets: ${uploadErrors.join(" | ")}`)
        };
    };

    const renderAvatar = (avatarUrl, fallbackText, className) => {
        const initial = (fallbackText || "").slice(0, 1).toUpperCase() || "U";

        if (avatarUrl) {
            return (
                <div className={`${className} home-avatar-circle-image-wrap`}>
                    <img src={avatarUrl} alt={fallbackText || "User"} className="home-avatar-image" />
                </div>
            );
        }

        return <div className={className}>{initial}</div>;
    };

    const getProfilePath = (userId) => {
        if (!userId) return "/explore";
        return currentUser?.id === userId ? "/profile" : `/player/${userId}`;
    };

    const getOrgIdFromVisibility = (visibility) => {
        if (typeof visibility !== "string") return null;
        const prefix = "org:";
        if (!visibility.startsWith(prefix)) return null;
        const orgId = visibility.slice(prefix.length).trim();
        return orgId || null;
    };

    const getOrgIdFromPostBody = (body) => {
        if (typeof body !== "string") return null;
        const match = body.match(/\n\n\[posted_as_org:([^\]]+)\]\s*$/i);
        return match?.[1]?.trim() || null;
    };

    const stripOrgPostMarker = (body) => {
        if (typeof body !== "string") return "";
        return body.replace(/\n\n\[posted_as_org:[^\]]+\]\s*$/i, "").trimEnd();
    };

    const canDeletePost = (post) => {
        if (!currentUser?.id || !post) return false;
        return post.authorId === currentUser.id;
    };

    const loadFeedPosts = useCallback(async () => {
        setFeedLoading(true);
        setFeedError("");

        const { data, error } = await getPosts(25);
        if (error) {
            console.error("Error loading posts:", error);
            setFeedError("Failed to load posts.");
            setFeedPosts([]);
            setFeedLoading(false);
            return;
        }

        const postsList = data || [];

        const authorIds = Array.from(new Set(postsList
            .map((post) => post.author_id)
            .filter(Boolean)));

        const orgAuthorIds = Array.from(new Set(postsList
            .map((post) => getOrgIdFromVisibility(post.visibility) || getOrgIdFromPostBody(post.body))
            .filter(Boolean)));

        let authorMap = {};
        let playerDetailsMap = {};
        let orgDetailsMap = {};
        let latestTeamByUserId = {};
        if (authorIds.length > 0) {
            const { data: profiles, error: profilesError } = await supabase
                .from("user_profiles")
                .select("user_id, first_name, last_name, avatar_url")
                .in("user_id", authorIds);

            if (profilesError) {
                console.error("Error loading author profiles:", profilesError);
            } else {
                authorMap = (profiles || []).reduce((acc, profile) => {
                    const name = `${profile.first_name || ""} ${profile.last_name || ""}`.trim();
                    acc[profile.user_id] = {
                        name: name || "User",
                        avatarUrl: profile.avatar_url || ""
                    };
                    return acc;
                }, {});
            }
        }

        if (authorIds.length > 0) {
            const { data: players, error: playersError } = await supabase
                .from("player_profiles")
                .select("user_id, firstname, lastname, positions, city, avatar_url")
                .in("user_id", authorIds);

            if (playersError) {
                console.error("Error loading player profiles:", playersError);
            } else {
                (players || []).forEach((player) => {
                    const name = `${player.firstname || ""} ${player.lastname || ""}`.trim();
                    if (!authorMap[player.user_id] || authorMap[player.user_id]?.name === "User") {
                        if (name) {
                            authorMap[player.user_id] = {
                                name,
                                avatarUrl: player.avatar_url || authorMap[player.user_id]?.avatarUrl || ""
                            };
                        }
                    }

                    playerDetailsMap[player.user_id] = {
                        positions: Array.isArray(player.positions) ? player.positions : [],
                        city: player.city || "",
                        avatarUrl: player.avatar_url || ""
                    };
                });
            }

            const { data: experiences, error: experiencesError } = await supabase
                .from("player_experiences")
                .select("user_id, team_name, start_date, end_date")
                .in("user_id", authorIds)
                .order("start_date", { ascending: false });

            if (experiencesError) {
                console.error("Error loading player experience for post headers:", experiencesError);
            } else {
                const experienceGroups = (experiences || []).reduce((acc, item) => {
                    if (!item.user_id) return acc;
                    if (!acc[item.user_id]) acc[item.user_id] = [];
                    acc[item.user_id].push(item);
                    return acc;
                }, {});

                Object.keys(experienceGroups).forEach((userId) => {
                    const entries = experienceGroups[userId];
                    if (!Array.isArray(entries) || entries.length === 0) return;

                    const currentTeam = entries.find((entry) => !entry.end_date && entry.team_name);
                    const latestTeam = entries.find((entry) => entry.team_name);
                    if (currentTeam?.team_name) {
                        latestTeamByUserId[userId] = currentTeam.team_name;
                    } else if (latestTeam?.team_name) {
                        latestTeamByUserId[userId] = latestTeam.team_name;
                    }
                });
            }
        }

        if (orgAuthorIds.length > 0) {
            const { data: orgProfiles, error: orgProfilesError } = await supabase
                .from("org_profiles")
                .select("id, name, city, type, avatar_url")
            .in("id", orgAuthorIds);

            if (orgProfilesError) {
                console.error("Error loading org profiles for post headers:", orgProfilesError);
            } else {
                (orgProfiles || []).forEach((org) => {
                    orgDetailsMap[org.id] = {
                        name: org.name || "Organization",
                        city: org.city || "",
                        type: org.type || "Organization",
                        avatarUrl: org.avatar_url || ""
                    };

                    if (!authorMap[org.id] || authorMap[org.id]?.name === "User") {
                        authorMap[org.id] = {
                            name: org.name || "Organization",
                            avatarUrl: org.avatar_url || ""
                        };
                    }
                });
            }
        }

        const postIds = (data || []).map((post) => post.id);
        let reactionCounts = {};
        let commentCounts = {};
        let likedPostIds = new Set();

        if (postIds.length > 0) {
            const { data: reactionRows, error: reactionError } = await supabase
                .from("post_reactions")
                .select("post_id, user_id")
                .in("post_id", postIds);

            if (reactionError) {
                console.error("Error loading reactions:", reactionError);
            } else {
                (reactionRows || []).forEach((row) => {
                    reactionCounts[row.post_id] = (reactionCounts[row.post_id] || 0) + 1;
                    if (currentUser && row.user_id === currentUser.id) {
                        likedPostIds.add(row.post_id);
                    }
                });
            }

            const { data: commentRows, error: commentError } = await supabase
                .from("post_comments")
                .select("post_id")
                .in("post_id", postIds)
                .is("deleted_at", null);

            if (commentError) {
                console.error("Error loading comment counts:", commentError);
            } else {
                (commentRows || []).forEach((row) => {
                    commentCounts[row.post_id] = (commentCounts[row.post_id] || 0) + 1;
                });
            }
        }

        const mapped = postsList.map((post) => {
            const orgAuthorId = getOrgIdFromVisibility(post.visibility) || getOrgIdFromPostBody(post.body);
            const orgAuthor = orgAuthorId ? orgDetailsMap[orgAuthorId] : null;
            const media = Array.isArray(post.post_media)
                ? [...post.post_media]
                    .sort((a, b) => (a.position || 0) - (b.position || 0))
                    .map((item) => ({ type: item.media_type, url: item.url }))
                : [];

            return {
                id: post.id,
                author: orgAuthor?.name || authorMap[post.author_id]?.name || "User",
                authorId: post.author_id,
                orgId: orgAuthorId || null,
                authorAvatarUrl: orgAuthor?.avatarUrl || playerDetailsMap[post.author_id]?.avatarUrl || authorMap[post.author_id]?.avatarUrl || "",
                time: formatTimeAgo(post.created_at),
                authorHeadline: orgAuthor
                    ? [
                        orgAuthor?.type || "Organization",
                        orgAuthor?.city || "",
                        formatTimeAgo(post.created_at)
                    ].filter(Boolean).join(" · ")
                    : [
                        playerDetailsMap[post.author_id]?.positions?.[0] || "",
                        latestTeamByUserId[post.author_id] || "",
                        formatTimeAgo(post.created_at)
                    ].filter(Boolean).join(" · "),
                body: stripOrgPostMarker(post.body || ""),
                media,
                authoredByOrg: !!orgAuthor,
                likedByCurrentUser: likedPostIds.has(post.id),
                stats: {
                    reactions: reactionCounts[post.id] || 0,
                    comments: commentCounts[post.id] || 0
                }
            };
        });

        setFeedPosts(mapped);
        setFeedLoading(false);
    }, [currentUser]);

    useEffect(() => {
        const getUser = async () => {
            try {
                const { data } = await supabase.auth.getSession();
                setCurrentUser(data?.session?.user ?? null);
            } catch (error) {
                console.error("Error fetching user:", error);
            } finally {
                setLoading(false);
            }
        };

        getUser();
    }, []);

    useEffect(() => {
        if (!currentUser) return;

        let isMounted = true;

        const loadProfileSummary = async () => {
            const metaFirst = currentUser?.user_metadata?.first_name || currentUser?.user_metadata?.firstname || "";
            const metaLast = currentUser?.user_metadata?.last_name || currentUser?.user_metadata?.lastname || "";
            const metaName = `${metaFirst} ${metaLast}`.trim();
            const metaAccountTypeRaw = currentUser?.user_metadata?.role || "";
            const metaAccountType = metaAccountTypeRaw
                ? metaAccountTypeRaw.toString().replace(/\b\w/g, (c) => c.toUpperCase())
                : "";

            let name = metaName || "Your Profile";
            let role = metaAccountType || "";
            let position = "";
            let location = "";
            let avatarUrl = currentUser?.user_metadata?.avatar_url || "";

            try {
                const { data: userProfile } = await supabase
                    .from("user_profiles")
                    .select("first_name, last_name, role, city, avatar_url")
                    .eq("user_id", currentUser.id)
                    .maybeSingle();

                if (userProfile) {
                    const fullName = `${userProfile.first_name || ""} ${userProfile.last_name || ""}`.trim();
                    if (fullName) name = fullName;
                    if (userProfile.role) {
                        role = userProfile.role.toString().replace(/\b\w/g, (c) => c.toUpperCase());
                    }
                    if (userProfile.city) location = userProfile.city;
                    if (userProfile.avatar_url) avatarUrl = userProfile.avatar_url;
                }
            } catch (error) {
                console.error("Error loading user profile summary:", error);
            }

            try {
                const { data: playerProfile } = await supabase
                    .from("player_profiles")
                    .select("firstname, lastname, positions, city, avatar_url")
                    .eq("user_id", currentUser.id)
                    .maybeSingle();

                if (playerProfile) {
                    const playerFullName = `${playerProfile.firstname || ""} ${playerProfile.lastname || ""}`.trim();
                    if (!name || name === "Your Profile") {
                        if (playerFullName) name = playerFullName;
                    }
                    if (Array.isArray(playerProfile.positions) && playerProfile.positions.length > 0) {
                        position = playerProfile.positions[0];
                    }
                    if (!location && playerProfile.city) location = playerProfile.city;
                    if (playerProfile.avatar_url) avatarUrl = playerProfile.avatar_url;

                    // Player profile existence means this account type is Player.
                    role = "Player";
                }
            } catch (error) {
                console.error("Error loading player profile summary:", error);
            }

            if (!role) role = "Member";

            if (!isMounted) return;
            setProfileSummary({ name, role, position, location, avatarUrl });
        };

        loadProfileSummary();

        return () => {
            isMounted = false;
        };
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser?.id) {
            setOrgPostIdentity(null);
            return;
        }

        let isMounted = true;

        const loadOrgIdentityForPosting = async () => {
            try {
                const { data: membership, error: membershipError } = await supabase
                    .from("org_memberships")
                    .select("org_id, orgrole")
                    .eq("user_id", currentUser.id)
                    .eq("orgrole", "admin")
                    .limit(1)
                    .maybeSingle();

                if (membershipError || !membership?.org_id) {
                    if (isMounted) setOrgPostIdentity(null);
                    return;
                }

                const { data: org, error: orgError } = await supabase
                    .from("org_profiles")
                    .select("id, name, city, type")
                    .eq("id", membership.org_id)
                    .maybeSingle();

                if (orgError || !org) {
                    if (isMounted) setOrgPostIdentity(null);
                    return;
                }

                if (isMounted) {
                    setOrgPostIdentity({
                        id: org.id,
                        name: org.name || "Organization",
                        city: org.city || "",
                        type: org.type || "Organization",
                    });
                }
            } catch (error) {
                console.error("Error loading org post identity:", error);
                if (isMounted) setOrgPostIdentity(null);
            }
        };

        loadOrgIdentityForPosting();

        return () => {
            isMounted = false;
        };
    }, [currentUser?.id]);

    useEffect(() => {
        if (!currentUser?.id) {
            setSelectedPostAs("");
            return;
        }

        const userPostValue = `user:${currentUser.id}`;

        if (!selectedPostAs) {
            setSelectedPostAs(userPostValue);
            return;
        }

        if (selectedPostAs.startsWith("org:") && !orgPostIdentity?.id) {
            setSelectedPostAs(userPostValue);
        }
    }, [currentUser?.id, orgPostIdentity?.id, selectedPostAs]);

    useEffect(() => {
        loadFeedPosts();
    }, [loadFeedPosts]);

    useEffect(() => {
        let cancelled = false;

        const loadFocusedPost = async () => {
            if (!focusPostId) {
                if (!cancelled) setFocusedPreviewPost(null);
                return;
            }

            const alreadyInFeed = feedPosts.some((post) => post.id === focusPostId);
            if (alreadyInFeed) {
                if (!cancelled) setFocusedPreviewPost(null);
                return;
            }

            if (focusPostPreview?.id === focusPostId) {
                const previewMedia = Array.isArray(focusPostPreview.media) ? focusPostPreview.media : [];
                if (!cancelled) {
                    setFocusedPreviewPost({
                        id: focusPostId,
                        author: focusPostPreview.author || "User",
                        authorId: focusPostPreview.authorId || null,
                        authorAvatarUrl: focusPostPreview.authorAvatarUrl || "",
                        time: formatTimeAgo(focusPostPreview.created_at),
                        authorHeadline: focusPostPreview.authorHeadline || formatTimeAgo(focusPostPreview.created_at),
                        body: focusPostPreview.body || "",
                        media: previewMedia,
                        likedByCurrentUser: false,
                        stats: focusPostPreview.stats || { reactions: 0, comments: 0 }
                    });
                }
                return;
            }

            const { data: rawPost, error: postError } = await supabase
                .from("posts")
                .select("id, author_id, body, created_at, post_media(id, media_type, url, position)")
                .eq("id", focusPostId)
                .is("deleted_at", null)
                .maybeSingle();

            if (postError || !rawPost || cancelled) {
                if (!cancelled) setFocusedPreviewPost(null);
                return;
            }

            let author = "User";
            let authorAvatarUrl = "";
            const rawOrgId = getOrgIdFromVisibility(rawPost.visibility) || getOrgIdFromPostBody(rawPost.body);

            if (rawOrgId) {
                const { data: orgProfile } = await supabase
                    .from("org_profiles")
                    .select("id, name, city, type, avatar_url")
                    .eq("id", rawOrgId)
                    .maybeSingle();

                if (orgProfile) {
                    author = orgProfile.name || "Organization";
                    authorAvatarUrl = orgProfile.avatar_url || "";
                }
            }

            const { data: up } = await supabase
                .from("user_profiles")
                .select("first_name, last_name, avatar_url")
                .eq("user_id", rawPost.author_id)
                .maybeSingle();

            if (up && !rawOrgId) {
                const fullName = `${up.first_name || ""} ${up.last_name || ""}`.trim();
                if (fullName) author = fullName;
                if (up.avatar_url) authorAvatarUrl = up.avatar_url;
            }

            const { data: pp } = await supabase
                .from("player_profiles")
                .select("firstname, lastname, avatar_url")
                .eq("user_id", rawPost.author_id)
                .maybeSingle();

            if (pp && !rawOrgId) {
                const fullName = `${pp.firstname || ""} ${pp.lastname || ""}`.trim();
                if (fullName && author === "User") author = fullName;
                if (pp.avatar_url && !authorAvatarUrl) authorAvatarUrl = pp.avatar_url;
            }

            const media = Array.isArray(rawPost.post_media)
                ? [...rawPost.post_media]
                    .sort((a, b) => (a.position || 0) - (b.position || 0))
                    .map((item) => ({ type: item.media_type, url: item.url }))
                : [];

            if (!cancelled) {
                setFocusedPreviewPost({
                    id: rawPost.id,
                    author,
                    authorId: rawPost.author_id,
                    orgId: rawOrgId || null,
                    authorAvatarUrl,
                    time: formatTimeAgo(rawPost.created_at),
                    authorHeadline: rawOrgId ? `Organization · ${formatTimeAgo(rawPost.created_at)}` : formatTimeAgo(rawPost.created_at),
                    body: stripOrgPostMarker(rawPost.body || ""),
                    media,
                    likedByCurrentUser: false,
                    stats: { reactions: 0, comments: 0 }
                });
            }
        };

        loadFocusedPost();

        return () => {
            cancelled = true;
        };
    }, [focusPostId, focusPostPreview, feedPosts]);

    useEffect(() => {
        if (!focusPostId) {
            setHighlightedPostId(null);
            return;
        }

        setHighlightedPostId(focusPostId);
        const timeoutId = window.setTimeout(() => {
            setHighlightedPostId((currentId) => (currentId === focusPostId ? null : currentId));
        }, 3000);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [focusPostId]);

    const orderedFeedPosts = focusPostId
        ? [...feedPosts].sort((a, b) => (a.id === focusPostId ? -1 : b.id === focusPostId ? 1 : 0))
        : feedPosts;

    const postsToRender = focusedPreviewPost
        ? [focusedPreviewPost, ...orderedFeedPosts.filter((post) => post.id !== focusedPreviewPost.id)]
        : orderedFeedPosts;

    useEffect(() => {
        if (!currentUser) {
            setSuggestedConnections([]);
            return;
        }

        let isMounted = true;

        const loadSuggestedConnections = async () => {
            setSuggestionsLoading(true);

            try {
                const relationships = await getConnectionsForCurrentUser();
                const excludedUserIds = new Set([currentUser.id]);

                (relationships || []).forEach((connection) => {
                    if (connection.requester_id) excludedUserIds.add(connection.requester_id);
                    if (connection.addressee_id) excludedUserIds.add(connection.addressee_id);
                });

                const { data: playerProfiles, error: playerProfilesError } = await supabase
                    .from("player_profiles")
                    .select("user_id, firstname, lastname, positions, city, avatar_url")
                    .limit(300);

                if (playerProfilesError) {
                    throw playerProfilesError;
                }

                const playerProfilesById = (playerProfiles || []).reduce((acc, profile) => {
                    if (profile.user_id) acc[profile.user_id] = profile;
                    return acc;
                }, {});

                const candidateIds = Object.keys(playerProfilesById).filter(
                    (userId) => userId && !excludedUserIds.has(userId)
                );

                const { data: userProfiles } = await supabase
                    .from("user_profiles")
                    .select("user_id, first_name, last_name, city, avatar_url")
                    .in("user_id", candidateIds);

                const userProfilesById = (userProfiles || []).reduce((acc, profile) => {
                    if (profile.user_id) acc[profile.user_id] = profile;
                    return acc;
                }, {});

                const mapped = candidateIds.map((userId) => {
                        const profile = userProfilesById[userId] || {};
                        const playerProfile = playerProfilesById[userId] || null;
                        const userName = `${profile.first_name || ""} ${profile.last_name || ""}`.trim();
                        const playerName = playerProfile
                            ? `${playerProfile.firstname || ""} ${playerProfile.lastname || ""}`.trim()
                            : "";

                        const displayName = playerName || userName || "User";
                        const roleLabel = Array.isArray(playerProfile?.positions) && playerProfile.positions.length > 0
                            ? playerProfile.positions[0]
                            : "Player";
                        const location = profile.city || playerProfile?.city || "";

                        return {
                            userId,
                            name: displayName,
                            role: roleLabel,
                            location,
                            avatarUrl: playerProfile?.avatar_url || profile.avatar_url || "",
                        };
                    });

                const shuffled = [...mapped].sort(() => Math.random() - 0.5);
                if (isMounted) {
                    setSuggestedConnections(shuffled.slice(0, 4));
                }
            } catch (error) {
                console.error("Error loading suggested connections:", error);
                if (isMounted) setSuggestedConnections([]);
            } finally {
                if (isMounted) setSuggestionsLoading(false);
            }
        };

        loadSuggestedConnections();

        return () => {
            isMounted = false;
        };
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser) {
            setSuggestedScouts([]);
            return;
        }

        let isMounted = true;

        const loadSuggestedScouts = async () => {
            setScoutsLoading(true);

            try {
                const relationships = await getConnectionsForCurrentUser();
                const excludedUserIds = new Set([currentUser.id]);

                (relationships || []).forEach((connection) => {
                    if (connection.requester_id) excludedUserIds.add(connection.requester_id);
                    if (connection.addressee_id) excludedUserIds.add(connection.addressee_id);
                });

                const { data: scoutProfiles, error: scoutProfilesError } = await supabase
                    .from("user_profiles")
                    .select("user_id, first_name, last_name, role, city, avatar_url")
                    .in("role", ["scout", "coach"])
                    .limit(300);

                if (scoutProfilesError) {
                    throw scoutProfilesError;
                }

                const available = (scoutProfiles || []).filter(
                    (profile) => profile.user_id && !excludedUserIds.has(profile.user_id)
                );

                const availableIds = available.map((profile) => profile.user_id);
                let orgNameByUserId = {};

                if (availableIds.length > 0) {
                    const { data: memberships, error: membershipError } = await supabase
                        .from("org_memberships")
                        .select("user_id, org_id")
                        .in("user_id", availableIds);

                    if (!membershipError && memberships && memberships.length > 0) {
                        const uniqueOrgIds = Array.from(new Set(memberships.map((m) => m.org_id).filter(Boolean)));

                        if (uniqueOrgIds.length > 0) {
                            const { data: orgs } = await supabase
                                .from("org_profiles")
                                .select("id, name")
                                .in("id", uniqueOrgIds);

                            const orgById = (orgs || []).reduce((acc, org) => {
                                if (org.id) acc[org.id] = org.name || "";
                                return acc;
                            }, {});

                            orgNameByUserId = memberships.reduce((acc, membership) => {
                                if (!acc[membership.user_id] && membership.org_id) {
                                    acc[membership.user_id] = orgById[membership.org_id] || "";
                                }
                                return acc;
                            }, {});
                        }
                    }
                }

                const mapped = available.map((profile) => {
                    const displayName = `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || "User";
                    const normalizedRole = (profile.role || "scout").toString().toLowerCase();
                    const roleLabel = normalizedRole.charAt(0).toUpperCase() + normalizedRole.slice(1);

                    return {
                        userId: profile.user_id,
                        name: displayName,
                        role: roleLabel,
                        city: profile.city || "",
                        orgName: orgNameByUserId[profile.user_id] || "",
                        avatarUrl: profile.avatar_url || "",
                    };
                });

                const shuffled = [...mapped].sort(() => Math.random() - 0.5);
                if (isMounted) {
                    setSuggestedScouts(shuffled.slice(0, 4));
                }
            } catch (error) {
                console.error("Error loading suggested scouts/coaches:", error);
                if (isMounted) setSuggestedScouts([]);
            } finally {
                if (isMounted) setScoutsLoading(false);
            }
        };

        loadSuggestedScouts();

        return () => {
            isMounted = false;
        };
    }, [currentUser]);

    const handleSendConnectionRequest = async (targetUserId) => {
        if (!targetUserId || connectLoadingUserId) return;

        setConnectLoadingUserId(targetUserId);
        try {
            await sendConnectionRequest(targetUserId);
            setSuggestedConnections((prev) => prev.filter((profile) => profile.userId !== targetUserId));
        } catch (error) {
            console.error("Error sending connection request:", error);
            alert("Could not send connection request. Please try again.");
        } finally {
            setConnectLoadingUserId("");
        }
    };

    const handleToggleLike = async (postId) => {
        if (!currentUser || likeLoadingPostId) return;

        const post = feedPosts.find((item) => item.id === postId);
        if (!post) return;

        setLikeLoadingPostId(postId);
        const isLiked = !!post.likedByCurrentUser;

        setFeedPosts((prev) => prev.map((item) => {
            if (item.id !== postId) return item;
            return {
                ...item,
                likedByCurrentUser: !isLiked,
                stats: {
                    ...item.stats,
                    reactions: Math.max(0, item.stats.reactions + (isLiked ? -1 : 1))
                }
            };
        }));

        try {
            if (isLiked) {
                await removeReaction(postId, currentUser.id);
            } else {
                await addReaction(postId, currentUser.id, "like");
            }
        } catch (error) {
            console.error("Error toggling like:", error);
            setFeedPosts((prev) => prev.map((item) => {
                if (item.id !== postId) return item;
                return {
                    ...item,
                    likedByCurrentUser: isLiked,
                    stats: {
                        ...item.stats,
                        reactions: Math.max(0, item.stats.reactions + (isLiked ? 1 : -1))
                    }
                };
            }));
        } finally {
            setLikeLoadingPostId(null);
        }
    };

    const loadCommentsForPost = async (postId) => {
        setCommentsLoadingByPost((prev) => ({ ...prev, [postId]: true }));

        try {
            const { data, error } = await getComments(postId);
            if (error) {
                console.error("Error loading comments:", error);
                setCommentsByPost((prev) => ({ ...prev, [postId]: [] }));
                return;
            }

            const authorIds = Array.from(new Set((data || []).map((item) => item.author_id).filter(Boolean)));
            let authorMap = {};

            if (authorIds.length > 0) {
                const { data: userProfiles } = await supabase
                    .from("user_profiles")
                    .select("user_id, first_name, last_name")
                    .in("user_id", authorIds);

                (userProfiles || []).forEach((profile) => {
                    const fullName = `${profile.first_name || ""} ${profile.last_name || ""}`.trim();
                    if (fullName) authorMap[profile.user_id] = fullName;
                });

                const { data: playerProfiles } = await supabase
                    .from("player_profiles")
                    .select("user_id, firstname, lastname")
                    .in("user_id", authorIds);

                (playerProfiles || []).forEach((profile) => {
                    if (!authorMap[profile.user_id]) {
                        const fullName = `${profile.firstname || ""} ${profile.lastname || ""}`.trim();
                        if (fullName) authorMap[profile.user_id] = fullName;
                    }
                });
            }

            const mapped = (data || []).map((comment) => ({
                ...comment,
                author_name: authorMap[comment.author_id] || "User",
                time: formatTimeAgo(comment.created_at)
            }));

            setCommentsByPost((prev) => ({ ...prev, [postId]: mapped }));
        } catch (error) {
            console.error("Error loading comments:", error);
        } finally {
            setCommentsLoadingByPost((prev) => ({ ...prev, [postId]: false }));
        }
    };

    const handleToggleComments = async (postId) => {
        const isExpanded = !!expandedComments[postId];
        if (isExpanded) {
            setExpandedComments((prev) => ({ ...prev, [postId]: false }));
            return;
        }

        setExpandedComments((prev) => ({ ...prev, [postId]: true }));
        if (!commentsByPost[postId]) {
            await loadCommentsForPost(postId);
        }
    };

    const handleOpenPostModal = async (post) => {
        setActivePostModal(post);
        setActivePostMediaIndex(0);
        if (!commentsByPost[post.id]) {
            await loadCommentsForPost(post.id);
        }
    };

    const handleClosePostModal = () => {
        setActivePostModal(null);
        setActivePostMediaIndex(0);
    };

    const handleSubmitComment = async (postId) => {
        const draft = (commentDraftByPost[postId] || "").trim();
        if (!currentUser || !draft || commentSubmittingPostId) return;

        setCommentSubmittingPostId(postId);
        try {
            const { error } = await addComment(postId, currentUser.id, draft);
            if (error) {
                console.error("Error adding comment:", error);
                return;
            }

            setCommentDraftByPost((prev) => ({ ...prev, [postId]: "" }));
            setFeedPosts((prev) => prev.map((item) => {
                if (item.id !== postId) return item;
                return {
                    ...item,
                    stats: { ...item.stats, comments: item.stats.comments + 1 }
                };
            }));
            await loadCommentsForPost(postId);
        } catch (error) {
            console.error("Error submitting comment:", error);
        } finally {
            setCommentSubmittingPostId(null);
        }
    };

    const handleDeletePost = async (post) => {
        if (!post?.id || deletingPostId) return;
        if (!canDeletePost(post)) {
            alert("You do not have permission to delete this post.");
            return;
        }

        if (!window.confirm("Delete this post? This cannot be undone.")) return;

        setDeletingPostId(post.id);
        setOpenPostMenuId("");
        try {
            const { error, cleanup } = await deletePostWithMediaCleanup(post.id, post.media || []);
            if (error) {
                console.error("Error deleting post:", error);
                alert("Failed to delete post.");
                return;
            }

            if (cleanup?.failures?.length) {
                console.warn("Post deleted but some media files were not removed:", cleanup.failures);
            }

            setFeedPosts((prev) => prev.filter((item) => item.id !== post.id));
            setExpandedComments((prev) => {
                const next = { ...prev };
                delete next[post.id];
                return next;
            });
            setCommentsByPost((prev) => {
                const next = { ...prev };
                delete next[post.id];
                return next;
            });
            setCommentDraftByPost((prev) => {
                const next = { ...prev };
                delete next[post.id];
                return next;
            });
            if (activePostModal?.id === post.id) {
                handleClosePostModal();
            }
            if (focusedPreviewPost?.id === post.id) {
                setFocusedPreviewPost(null);
            }
            if (highlightedPostId === post.id) {
                setHighlightedPostId(null);
            }
            if (openPostMenuId === post.id) {
                setOpenPostMenuId("");
            }
        } catch (error) {
            console.error("Error deleting post:", error);
            alert("Failed to delete post.");
        } finally {
            setDeletingPostId("");
        }
    };

    const handleOpenSend = async (post) => {
        if (!currentUser) return;
        setSendLoading(true);

        try {
            const { data, error } = await listConversations(currentUser.id);
            if (error) {
                console.error("Error loading conversations:", error);
                alert("Could not load recipients right now.");
                return;
            }

            const acceptedConversations = (data || []).filter((conv) => conv.status === "accepted");
            const targetIds = Array.from(new Set(acceptedConversations.map((conv) => (
                conv.initiator_id === currentUser.id ? conv.recipient_id : conv.initiator_id
            ))));

            if (targetIds.length === 0) {
                alert("You need an accepted conversation to send a post.");
                return;
            }

            const { data: userProfiles } = await supabase
                .from("user_profiles")
                .select("user_id, first_name, last_name")
                .in("user_id", targetIds);

            const nameMap = {};
            (userProfiles || []).forEach((profile) => {
                const fullName = `${profile.first_name || ""} ${profile.last_name || ""}`.trim();
                if (fullName) nameMap[profile.user_id] = fullName;
            });

            const { data: playerProfiles } = await supabase
                .from("player_profiles")
                .select("user_id, firstname, lastname")
                .in("user_id", targetIds);

            (playerProfiles || []).forEach((profile) => {
                if (!nameMap[profile.user_id]) {
                    const fullName = `${profile.firstname || ""} ${profile.lastname || ""}`.trim();
                    if (fullName) nameMap[profile.user_id] = fullName;
                }
            });

            const targets = acceptedConversations.map((conv) => {
                const userId = conv.initiator_id === currentUser.id ? conv.recipient_id : conv.initiator_id;
                return {
                    conversationId: conv.id,
                    userId,
                    label: nameMap[userId] || "User"
                };
            });

            setSendTargets(targets);
            setSelectedSendTarget(targets[0]?.conversationId || "");
            setSendModalPost(post);
        } finally {
            setSendLoading(false);
        }
    };

    const handleSendPost = async () => {
        if (!currentUser || !sendModalPost || !selectedSendTarget || sendLoading) return;
        setSendLoading(true);

        try {
            const snippet = (sendModalPost.body || "").slice(0, 180);
            const messageBody = `Shared a post:\n\n${snippet || "[No text content]"}${sendModalPost.body.length > 180 ? "..." : ""}\n\n[shared_post:${sendModalPost.id}]`;
            const { error } = await sendMessage(selectedSendTarget, currentUser.id, messageBody);
            if (error) {
                console.error("Error sending post:", error);
                alert("Failed to send post.");
                return;
            }

            setSendModalPost(null);
            setSelectedSendTarget("");
            setSendTargets([]);
        } catch (error) {
            console.error("Error sending post:", error);
            alert("Failed to send post.");
        } finally {
            setSendLoading(false);
        }
    };

    const composerDisplayName = (
        profileSummary?.name
        || `${currentUser?.user_metadata?.first_name || currentUser?.user_metadata?.firstname || ""} ${currentUser?.user_metadata?.last_name || currentUser?.user_metadata?.lastname || ""}`.trim()
        || currentUser?.email
        || "You"
    );

    const postAsOptions = [
        {
            value: currentUser?.id ? `user:${currentUser.id}` : "user",
            label: composerDisplayName,
            subtitle: "My profile",
            mode: "user",
        },
        ...(orgPostIdentity?.id ? [{
            value: `org:${orgPostIdentity.id}`,
            label: orgPostIdentity.name,
            subtitle: "Organization profile",
            mode: "org",
            orgId: orgPostIdentity.id,
        }] : []),
    ];

    const selectedPostAsOption = postAsOptions.find((option) => option.value === selectedPostAs) || postAsOptions[0];

    if (loading) {
        return (
            <main className="page home-feed-page">
                <section className="home-feed-card">
                    <p>Loading...</p>
                </section>
            </main>
        );
    }

    if (!currentUser) {
        return (
            <main className="page home-feed-page home-feed-page--landing">
                <div className="home-landing-container">
                    <div className="home-landing-content">
                        <p className="home-landing-eyebrow">Canadian football network</p>
                        <h1>Welcome to</h1>
                        <img src="/DribblLogo.jpg" alt="Dribbl logo" className="home-logo" />
                        <p>Connect with athletes, scouts, and coaches. Share your performance, discover talent, and build your network.</p>
                        <div className="home-landing-points">
                            <div className="home-landing-point">Create a complete player profile</div>
                            <div className="home-landing-point">Post clips, updates, and milestones</div>
                            <div className="home-landing-point">Get discovered and build connections</div>
                        </div>
                        <div className="home-landing-actions">
                            <Link to="/signup" className="home-landing-btn home-landing-btn-primary">Create Account</Link>
                            <Link to="/login" className="home-landing-btn home-landing-btn-secondary">Log In</Link>
                        </div>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="page home-feed-page">
            <div className="home-feed-layout">
                <aside className="home-feed-left">
                    {loading ? (
                        <div className="home-feed-card home-profile-card home-profile-skeleton">
                            <div className="home-profile-banner" />
                            <div className="home-profile-skeleton-avatar" />
                            <div className="home-profile-skeleton-name" />
                            <div className="home-profile-skeleton-role" />
                            <div className="home-profile-skeleton-meta" />
                        </div>
                    ) : (
                        <Link to="/profile" className="home-profile-link" aria-label="Go to profile">
                            <div className="home-feed-card home-profile-card">
                                <div className="home-profile-banner" />
                                {renderAvatar(profileSummary.avatarUrl, profileSummary.name, "home-profile-avatar")}
                                <div className="home-profile-name">{profileSummary.name}</div>
                                <div className="home-profile-role">{profileSummary.role}</div>
                                {(profileSummary.position || profileSummary.location) && (
                                    <div className="home-profile-meta">
                                        {profileSummary.position && <span>{profileSummary.position}</span>}
                                        {profileSummary.location && <span>{profileSummary.location}</span>}
                                    </div>
                                )}
                            </div>
                        </Link>
                    )}
                </aside>
                <section className="home-feed-main">
                    <div className="home-feed-card home-post-composer">
                        <div className="home-composer-header">
                            {renderAvatar(profileSummary.avatarUrl, profileSummary.name || currentUser?.email, "home-avatar-circle")}
                            <button 
                                className="home-composer-input" 
                                type="button"
                                onClick={() => setShowPostModal(true)}
                            >
                                Start a post
                            </button>
                        </div>
                    </div>

                    <div className="home-feed-label">Latest posts</div>
                    {highlightedPostId && (
                        <div className="home-feed-focus-banner">Featured post selected from message/profile activity</div>
                    )}

                    <div className="home-feed-list">
                        {feedLoading && (
                            <>
                                {[1, 2, 3].map((index) => (
                                    <div className="home-feed-card home-skeleton-card" key={index}>
                                        <div className="home-skeleton-header">
                                            <div className="home-skeleton-avatar" />
                                            <div className="home-skeleton-meta">
                                                <div className="home-skeleton-line home-skeleton-line--author" />
                                                <div className="home-skeleton-line home-skeleton-line--time" />
                                            </div>
                                        </div>
                                        <div className="home-skeleton-body">
                                            <div className="home-skeleton-line home-skeleton-line--full" />
                                            <div className="home-skeleton-line home-skeleton-line--full" />
                                            <div className="home-skeleton-line home-skeleton-line--half" />
                                        </div>
                                        <div className="home-skeleton-footer">
                                            <div className="home-skeleton-action" />
                                            <div className="home-skeleton-action" />
                                            <div className="home-skeleton-action" />
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                        {!feedLoading && feedError && (
                            <div className="home-feed-card">
                                <p>{feedError}</p>
                            </div>
                        )}
                        {!feedLoading && !feedError && feedPosts.length === 0 && (
                            <div className="home-feed-card">
                                <p>No posts yet. Be the first to share something.</p>
                            </div>
                        )}
                        {!feedLoading && !feedError && postsToRender.map((post) => (
                            <article className={`home-feed-card home-post-card ${highlightedPostId && post.id === highlightedPostId ? "home-post-card-featured" : ""}`} key={post.id}>
                                {canDeletePost(post) && (
                                    <div className="home-post-menu-wrap">
                                        <button
                                            className="home-post-menu-trigger"
                                            type="button"
                                            aria-label="Post actions"
                                            aria-expanded={openPostMenuId === post.id}
                                            onClick={() => setOpenPostMenuId((prev) => (prev === post.id ? "" : post.id))}
                                            disabled={deletingPostId === post.id}
                                        >
                                            ...
                                        </button>
                                        {openPostMenuId === post.id && (
                                            <div className="home-post-menu-dropdown">
                                                <button
                                                    className="home-post-menu-item home-post-menu-item-danger"
                                                    type="button"
                                                    onClick={() => handleDeletePost(post)}
                                                    disabled={deletingPostId === post.id}
                                                >
                                                    {deletingPostId === post.id ? "Deleting..." : "Delete"}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {highlightedPostId && post.id === highlightedPostId && (
                                    <div className="home-post-featured-pill">Featured</div>
                                )}
                                {post.authoredByOrg ? (
                                    <Link to={post.orgId ? `/org/${post.orgId}` : "#"} className="home-post-header home-profile-link-inline">
                                        {renderAvatar(post.authorAvatarUrl, post.author, "home-avatar-circle")}
                                        <div className="home-post-meta">
                                            <div className="home-post-author">{post.author}</div>
                                            <div className="home-post-headline">{post.authorHeadline || post.time}</div>
                                        </div>
                                    </Link>
                                ) : (
                                    <Link to={getProfilePath(post.authorId)} className="home-post-header home-profile-link-inline">
                                        {renderAvatar(post.authorAvatarUrl, post.author, "home-avatar-circle")}
                                        <div className="home-post-meta">
                                            <div className="home-post-author">{post.author}</div>
                                            <div className="home-post-headline">{post.authorHeadline || post.time}</div>
                                        </div>
                                    </Link>
                                )}
                                <p className="home-post-body">{post.body}</p>
                                {post.media.length > 0 && (
                                    <div
                                        className={`home-post-media ${post.media.length > 1 ? "home-post-media--slideshow" : "home-post-media--single"}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => handleOpenPostModal(post)}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                handleOpenPostModal(post);
                                            }
                                        }}
                                    >
                                        {post.media.map((item, index) => (
                                            item.type === "video" ? (
                                                <video key={item.url + index} src={item.url} controls preload="metadata" />
                                            ) : (
                                                <img key={item.url + index} src={item.url} alt="Post media" />
                                            )
                                        ))}
                                    </div>
                                )}
                                <footer className="home-post-footer">
                                    <div className="home-post-stats">
                                        <span>{post.stats.reactions} reactions</span>
                                        <span>{post.stats.comments} comments</span>
                                    </div>
                                    <div className="home-post-actions">
                                        <button
                                            className={`home-action-btn ${post.likedByCurrentUser ? "home-action-btn-active" : ""}`}
                                            type="button"
                                            onClick={() => handleToggleLike(post.id)}
                                            disabled={likeLoadingPostId === post.id || deletingPostId === post.id}
                                        >
                                            {post.likedByCurrentUser ? <VscHeartFilled /> : <VscHeart />}
                                            <span>{likeLoadingPostId === post.id ? "..." : "Like"}</span>
                                        </button>
                                        <button
                                            className="home-action-btn"
                                            type="button"
                                            onClick={() => handleToggleComments(post.id)}
                                            disabled={deletingPostId === post.id}
                                        >
                                            <VscComment />
                                            <span>Comment</span>
                                        </button>
                                        <button
                                            className="home-action-btn"
                                            type="button"
                                            onClick={() => handleOpenSend(post)}
                                            disabled={sendLoading || deletingPostId === post.id}
                                        >
                                            <VscSend />
                                            <span>Send</span>
                                        </button>
                                    </div>
                                </footer>

                                {expandedComments[post.id] && (
                                    <div className="home-post-comments-panel">
                                        {commentsLoadingByPost[post.id] ? (
                                            <div className="home-comment-empty">Loading comments...</div>
                                        ) : (
                                            <>
                                                <div className="home-post-comments-list">
                                                    {(commentsByPost[post.id] || []).length === 0 ? (
                                                        <div className="home-comment-empty">No comments yet. Be the first to comment.</div>
                                                    ) : (
                                                        (commentsByPost[post.id] || []).map((comment) => (
                                                            <div key={comment.id} className="home-post-comment-item">
                                                                <Link to={getProfilePath(comment.author_id)} className="home-profile-link-inline">
                                                                    <div className="home-avatar-circle home-avatar-circle-small">
                                                                        {(comment.author_name || "").slice(0, 1).toUpperCase()}
                                                                    </div>
                                                                </Link>
                                                                <div className="home-post-comment-content">
                                                                    <div className="home-post-comment-meta">
                                                                        <Link to={getProfilePath(comment.author_id)} className="home-post-comment-author home-profile-link-inline">
                                                                            {comment.author_name}
                                                                        </Link>
                                                                        <span className="home-post-comment-time">{comment.time}</span>
                                                                    </div>
                                                                    <p>{comment.body}</p>
                                                                </div>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                                <div className="home-post-comment-input-row">
                                                    <input
                                                        type="text"
                                                        placeholder="Write a comment"
                                                        value={commentDraftByPost[post.id] || ""}
                                                        onChange={(event) => setCommentDraftByPost((prev) => ({
                                                            ...prev,
                                                            [post.id]: event.target.value
                                                        }))}
                                                    />
                                                    <button
                                                        className="home-action-btn"
                                                        type="button"
                                                        onClick={() => handleSubmitComment(post.id)}
                                                        disabled={commentSubmittingPostId === post.id || !(commentDraftByPost[post.id] || "").trim()}
                                                    >
                                                        {commentSubmittingPostId === post.id ? "Posting..." : "Post"}
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </article>
                        ))}
                    </div>
                </section>

                <aside className="home-feed-sidebar">
                    <div className="home-feed-card home-sidebar-card">
                        <h3>People you may know</h3>
                        <div className="home-suggestion-list">
                            {suggestionsLoading && (
                                <div className="home-suggestion-row">Loading suggestions...</div>
                            )}
                            {!suggestionsLoading && suggestedConnections.length === 0 && (
                                <div className="home-suggestion-row">No new suggestions right now.</div>
                            )}
                            {!suggestionsLoading && suggestedConnections.map((profile) => (
                                <div className="home-suggestion-row" key={profile.userId}>
                                    <Link to={getProfilePath(profile.userId)} className="home-profile-link-inline home-suggestion-link">
                                        {renderAvatar(profile.avatarUrl, profile.name, "home-avatar-circle home-avatar-circle-small")}
                                    </Link>
                                    <Link to={getProfilePath(profile.userId)} className="home-profile-link-inline home-suggestion-link">
                                        <div className="home-suggestion-name">{profile.name}</div>
                                        <div className="home-suggestion-meta">
                                            {profile.role}
                                            {profile.location ? ` • ${profile.location}` : ""}
                                        </div>
                                    </Link>
                                    <button
                                        className="home-action-btn"
                                        type="button"
                                        onClick={() => handleSendConnectionRequest(profile.userId)}
                                        disabled={connectLoadingUserId === profile.userId}
                                    >
                                        {connectLoadingUserId === profile.userId ? "..." : "Connect"}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="home-feed-card home-sidebar-card">
                        <h3>Scouts & Coaches</h3>
                        <div className="home-suggestion-list">
                            {scoutsLoading && (
                                <div className="home-suggestion-row">Loading scouts and coaches...</div>
                            )}
                            {!scoutsLoading && suggestedScouts.length === 0 && (
                                <div className="home-suggestion-row">No new scouts or coaches right now.</div>
                            )}
                            {!scoutsLoading && suggestedScouts.map((profile) => (
                                <div className="home-suggestion-row" key={profile.userId}>
                                    <Link to={getProfilePath(profile.userId)} className="home-profile-link-inline home-suggestion-link">
                                        {renderAvatar(profile.avatarUrl, profile.name, "home-avatar-circle home-avatar-circle-small")}
                                    </Link>
                                    <Link to={getProfilePath(profile.userId)} className="home-profile-link-inline home-suggestion-link">
                                        <div className="home-suggestion-name">{profile.name}</div>
                                        <div className="home-suggestion-meta">
                                            {profile.role}
                                            {profile.orgName ? ` • ${profile.orgName}` : profile.city ? ` • ${profile.city}` : ""}
                                        </div>
                                    </Link>
                                    <button
                                        className="home-action-btn"
                                        type="button"
                                        onClick={() => handleSendConnectionRequest(profile.userId)}
                                        disabled={connectLoadingUserId === profile.userId}
                                    >
                                        {connectLoadingUserId === profile.userId ? "..." : "Connect"}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </aside>
            </div>

            {showPostModal && (
                <div
                    className="home-comment-modal-backdrop"
                    role="dialog"
                    aria-modal="true"
                    onClick={() => {
                        setShowPostModal(false);
                        setPostContent("");
                        setPostMediaFile(null);
                    }}
                >
                    <div className="home-comment-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="home-comment-modal-layout home-comment-modal-layout--no-media">
                            <div className="home-comment-modal-comments">
                                <div className="home-comment-panel-header">
                                    <div className="home-comment-author-row">
                                        {renderAvatar(profileSummary.avatarUrl, composerDisplayName, "home-avatar-circle")}
                                        <div>
                                            <div className="home-comment-modal-title">Create a post</div>
                                            <div className="home-comment-modal-subtitle">{composerDisplayName}</div>
                                        </div>
                                    </div>
                                    <button
                                        className="home-action-btn home-action-btn-ghost"
                                        type="button"
                                        onClick={() => {
                                            setShowPostModal(false);
                                            setPostContent("");
                                            setPostMediaFile(null);
                                        }}
                                        disabled={postSubmitting}
                                    >
                                        X
                                    </button>
                                </div>
                                <div className="home-comment-panel-scroll">
                                    {orgPostIdentity?.id && (
                                        <div className="home-post-as-row">
                                            <div className="home-post-media-label">Post as</div>
                                            <select
                                                className="home-post-as-select"
                                                value={selectedPostAs}
                                                onChange={(event) => setSelectedPostAs(event.target.value)}
                                                disabled={postSubmitting}
                                            >
                                                {postAsOptions.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label} ({option.subtitle})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    <div className="home-post-media-row">
                                        <div className="home-post-media-label">Media (optional)</div>
                                        <div className="home-post-media-controls home-post-media-controls--file">
                                            <button
                                                className="home-post-add-media-btn"
                                                type="button"
                                                onClick={() => {
                                                    if (postMediaInputRef.current) {
                                                        postMediaInputRef.current.value = "";
                                                    }
                                                    postMediaInputRef.current?.click();
                                                }}
                                                disabled={postSubmitting}
                                            >
                                                +
                                            </button>
                                            <div className="home-post-media-selected">
                                                {postMediaFile ? postMediaFile.name : "No media selected"}
                                            </div>
                                            {postMediaFile && (
                                                <button
                                                    className="home-post-remove-media-btn"
                                                    type="button"
                                                    onClick={() => setPostMediaFile(null)}
                                                    disabled={postSubmitting}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                        <input
                                            ref={postMediaInputRef}
                                            className="home-post-media-file-input"
                                            type="file"
                                            accept="image/*"
                                            onChange={(event) => {
                                                const file = event.target.files?.[0] || null;
                                                setPostMediaFile(file);
                                            }}
                                            disabled={postSubmitting}
                                        />
                                    </div>
                                    <textarea
                                        className="home-post-textarea"
                                        placeholder="What do you want to talk about?"
                                        value={postContent}
                                        onChange={(e) => setPostContent(e.target.value)}
                                        rows={8}
                                        disabled={postSubmitting}
                                        autoFocus
                                    />
                                </div>
                                <div className="home-post-modal-footer">
                                    <button
                                        className="home-action-btn"
                                        type="button"
                                        onClick={async () => {
                                            if (!postContent.trim() || postSubmitting) return;
                                            
                                            setPostSubmitting(true);
                                            try {
                                                const authorId = currentUser.id;
                                                const isOrgMode = selectedPostAsOption?.mode === "org" && selectedPostAsOption?.orgId;
                                                const bodyToSave = isOrgMode
                                                    ? `${postContent.trim()}\n\n[posted_as_org:${selectedPostAsOption.orgId}]`
                                                    : postContent.trim();

                                                const { data, error } = await createPost(authorId, bodyToSave, "public");
                                                if (error || !data) {
                                                    console.error("Error creating post:", error);
                                                    alert("Failed to create post. Please try again.");
                                                    return;
                                                }

                                                if (postMediaFile) {
                                                    const { data: uploadedMedia, error: uploadError } = await uploadPostMediaFile(
                                                        postMediaFile,
                                                        currentUser.id
                                                    );

                                                    if (uploadError) {
                                                        console.error("Error uploading media:", uploadError);
                                                        alert(`Post created, but media upload failed: ${uploadError.message || "Unknown storage error"}`);
                                                    }

                                                    if (uploadedMedia?.url && uploadedMedia?.mediaType) {
                                                        const { error: mediaError } = await addPostMedia(
                                                            data.id,
                                                            uploadedMedia.mediaType,
                                                            uploadedMedia.url,
                                                            0
                                                        );
                                                        if (mediaError) {
                                                            console.error("Error adding media:", mediaError);
                                                            alert("Post created, but media failed to save.");
                                                        }
                                                    }
                                                }

                                                console.log("Post created:", data);
                                                setShowPostModal(false);
                                                setPostContent("");
                                                setPostMediaFile(null);
                                                loadFeedPosts();
                                            } catch (err) {
                                                console.error("Exception creating post:", err);
                                                alert("Failed to create post. Please try again.");
                                            } finally {
                                                setPostSubmitting(false);
                                            }
                                        }}
                                        disabled={!postContent.trim() || postSubmitting}
                                    >
                                        {postSubmitting ? "Posting..." : "Post"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activePostModal && (
                <div
                    className="home-comment-modal-backdrop"
                    role="dialog"
                    aria-modal="true"
                    onClick={handleClosePostModal}
                >
                    <div className="home-comment-modal" onClick={(event) => event.stopPropagation()}>
                        <div className={`home-comment-modal-layout ${activePostModal.media?.length > 0 ? "home-comment-modal-layout--with-media" : "home-comment-modal-layout--no-media"}`}>
                            {activePostModal.media?.length > 0 && (
                                <div className="home-comment-modal-media">
                                    <div className="home-media-carousel">
                                        {activePostModal.media.length > 1 && (
                                            <button
                                                className="home-media-arrow home-media-arrow-left"
                                                type="button"
                                                onClick={() => setActivePostMediaIndex((prev) => (
                                                    (prev - 1 + activePostModal.media.length) % activePostModal.media.length
                                                ))}
                                                aria-label="Previous media"
                                            >
                                                ‹
                                            </button>
                                        )}

                                        {activePostModal.media[activePostMediaIndex]?.type === "video" ? (
                                            <video
                                                src={activePostModal.media[activePostMediaIndex].url}
                                                controls
                                                preload="metadata"
                                            />
                                        ) : (
                                            <img
                                                src={activePostModal.media[activePostMediaIndex]?.url}
                                                alt="Post media"
                                            />
                                        )}

                                        {activePostModal.media.length > 1 && (
                                            <button
                                                className="home-media-arrow home-media-arrow-right"
                                                type="button"
                                                onClick={() => setActivePostMediaIndex((prev) => (
                                                    (prev + 1) % activePostModal.media.length
                                                ))}
                                                aria-label="Next media"
                                            >
                                                ›
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="home-comment-modal-comments">
                                <div className="home-comment-panel-header">
                                    <div className="home-comment-author-row">
                                        <div className="home-avatar-circle">{activePostModal.author.slice(0, 1)}</div>
                                        <div>
                                            <div className="home-comment-modal-title">{activePostModal.author}</div>
                                            <div className="home-comment-modal-subtitle">{activePostModal.time}</div>
                                        </div>
                                    </div>
                                    <button
                                        className="home-action-btn home-action-btn-ghost"
                                        type="button"
                                        onClick={handleClosePostModal}
                                    >
                                        X
                                    </button>
                                </div>

                                <div className="home-comment-panel-scroll">
                                    <p className="home-comment-modal-body">{activePostModal.body}</p>

                                    <div className="home-post-comments-list">
                                        {(commentsByPost[activePostModal.id] || []).length === 0 ? (
                                            <div className="home-comment-empty">No comments yet. Be the first to comment.</div>
                                        ) : (
                                            (commentsByPost[activePostModal.id] || []).map((comment) => (
                                                <div key={comment.id} className="home-post-comment-item">
                                                    <div className="home-avatar-circle home-avatar-circle-small">
                                                        {(comment.author_name || "").slice(0, 1).toUpperCase()}
                                                    </div>
                                                    <div className="home-post-comment-content">
                                                        <div className="home-post-comment-meta">
                                                            <span className="home-post-comment-author">{comment.author_name}</span>
                                                            <span className="home-post-comment-time">{comment.time}</span>
                                                        </div>
                                                        <p>{comment.body}</p>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                <div className="home-post-comment-input-row">
                                    <input
                                        type="text"
                                        placeholder="Write a comment"
                                        value={commentDraftByPost[activePostModal.id] || ""}
                                        onChange={(event) => setCommentDraftByPost((prev) => ({
                                            ...prev,
                                            [activePostModal.id]: event.target.value
                                        }))}
                                    />
                                    <button
                                        className="home-action-btn"
                                        type="button"
                                        onClick={() => handleSubmitComment(activePostModal.id)}
                                        disabled={commentSubmittingPostId === activePostModal.id || !(commentDraftByPost[activePostModal.id] || "").trim()}
                                    >
                                        {commentSubmittingPostId === activePostModal.id ? "Posting..." : "Post"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {sendModalPost && (
                <div
                    className="home-comment-modal-backdrop"
                    role="dialog"
                    aria-modal="true"
                    onClick={() => {
                        if (sendLoading) return;
                        setSendModalPost(null);
                        setSelectedSendTarget("");
                        setSendTargets([]);
                    }}
                >
                    <div className="home-send-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="home-send-modal-header">
                            <h3>Send post</h3>
                            <button
                                className="home-action-btn home-action-btn-ghost"
                                type="button"
                                onClick={() => {
                                    setSendModalPost(null);
                                    setSelectedSendTarget("");
                                    setSendTargets([]);
                                }}
                                disabled={sendLoading}
                            >
                                X
                            </button>
                        </div>

                        <p className="home-send-modal-subtitle">Choose a conversation to share this post.</p>

                        <div className="home-send-target-list" role="listbox" aria-label="Send targets">
                            {sendTargets.map((target) => (
                                <button
                                    key={target.conversationId}
                                    type="button"
                                    className={`home-send-target-item ${selectedSendTarget === target.conversationId ? "active" : ""}`}
                                    onClick={() => setSelectedSendTarget(target.conversationId)}
                                    disabled={sendLoading}
                                >
                                    {target.label}
                                </button>
                            ))}
                        </div>

                        <div className="home-send-actions">
                            <button
                                className="home-action-btn home-action-btn-ghost"
                                type="button"
                                onClick={() => {
                                    setSendModalPost(null);
                                    setSelectedSendTarget("");
                                    setSendTargets([]);
                                }}
                                disabled={sendLoading}
                            >
                                Cancel
                            </button>
                            <button
                                className="home-action-btn"
                                type="button"
                                onClick={handleSendPost}
                                disabled={sendLoading || !selectedSendTarget}
                            >
                                {sendLoading ? "Sending..." : "Send"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </main>
    );
}

export default Home;
